import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Loader2,
  Pencil,
  RefreshCw,
  Settings,
  Wallet,
  X,
} from 'lucide-react';
import { platformFetch } from '../../platform/http';
import toast from '../Toast';
import BalanceTrendPanel from './upstreamBalance/BalanceTrendPanel';

type UpstreamBalanceSite = {
  key: string;
  name: string;
  host: string;
  origin: string;
  mode: 'newapi' | 'manual' | 'unknown';
  detectedMode: 'newapi' | 'manual' | 'unknown';
  balanceCny: number | null;
  quota: number | null;
  usedQuota: number | null;
  price: number | null;
  quotaPerUnit: number | null;
  thresholdCny: number;
  topUpUrl: string;
  manualUrl?: string;
  low: boolean;
  error?: string;
  retryAt?: string;
  channels: string[];
};

type UpstreamBalanceResult = {
  checkedAt: string;
  configComplete: boolean;
  configError?: string;
  sites: UpstreamBalanceSite[];
};

type UpstreamBalanceConfig = {
  username: string;
  passwordSet: boolean;
  sites: Record<string, unknown>;
};

function formatCny(value: number | null): string {
  return value == null ? '-' : `¥${value.toFixed(2)}`;
}

function formatTime(value: string): string {
  try {
    return new Date(value).toLocaleString('zh-CN', { hour12: false });
  } catch {
    return value;
  }
}

function modeBadge(mode: UpstreamBalanceSite['mode']) {
  if (mode === 'newapi') {
    return <span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">new-api</span>;
  }
  if (mode === 'manual') {
    return <span className="inline-flex rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-700">手动</span>;
  }
  return <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">未知</span>;
}

function statusBadge(site: UpstreamBalanceSite) {
  if (site.error) {
    return (
      <div className="space-y-1">
        <span className="inline-flex items-center gap-1 text-xs text-amber-700">
          <AlertTriangle className="h-3.5 w-3.5" />
          读取失败
        </span>
        <div className="max-w-[220px] text-xs leading-snug text-amber-700/90" title={site.error}>
          {site.error}
        </div>
        {site.retryAt && (
          <div className="text-xs text-light-muted">自动重试 {formatTime(site.retryAt)}</div>
        )}
      </div>
    );
  }
  if (site.mode === 'manual') {
    return <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">手动查看</span>;
  }
  if (site.low) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-red-700">
        <AlertTriangle className="h-3.5 w-3.5" />
        余额不足
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-green-700">
      <CheckCircle2 className="h-3.5 w-3.5" />
      正常
    </span>
  );
}

export default function UpstreamBalancesTab() {
  const [data, setData] = useState<UpstreamBalanceResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [configOpen, setConfigOpen] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordSet, setPasswordSet] = useState(false);
  const [sitesJson, setSitesJson] = useState('{}');
  const [apibUserId, setApibUserId] = useState('');
  const [apibCookie, setApibCookie] = useState('');

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draftValue, setDraftValue] = useState('');
  const [savingKey, setSavingKey] = useState<string | null>(null);
  /** 本次编辑是否已经提交过：Enter 失焦可能连续触发，避免重复发请求 */
  const editDoneRef = useRef(false);

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await platformFetch(`/admin/upstream-balances${refresh ? '?refresh=1' : ''}`);
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload?.error || '加载余额失败');
      }
      setData(payload as UpstreamBalanceResult);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : '加载余额失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(() => load(), 5 * 60 * 1000);
    return () => window.clearInterval(timer);
  }, [load]);

  const openConfig = async () => {
    setConfigOpen(true);
    setConfigLoading(true);
    try {
      const res = await platformFetch('/admin/upstream-balances/config');
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload?.error || '读取配置失败');
      }
      const config = payload as UpstreamBalanceConfig;
      setUsername(config.username || '');
      setPassword('');
      setPasswordSet(Boolean(config.passwordSet));
      setSitesJson(JSON.stringify(config.sites || {}, null, 2));
      const apibSite = (config.sites?.['apib.ai'] || {}) as { sessionCookie?: unknown; userId?: unknown };
      setApibUserId(typeof apibSite.userId === 'string' ? apibSite.userId : '');
      setApibCookie(typeof apibSite.sessionCookie === 'string' ? apibSite.sessionCookie : '');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '读取配置失败');
      setConfigOpen(false);
    } finally {
      setConfigLoading(false);
    }
  };

  const saveConfig = async () => {
    let sites: unknown;
    try {
      sites = JSON.parse(sitesJson || '{}');
    } catch {
      toast.error('站点覆盖不是合法的 JSON');
      return;
    }
    if (!sites || typeof sites !== 'object' || Array.isArray(sites)) {
      toast.error('站点覆盖必须是 JSON 对象');
      return;
    }
    const cookie = apibCookie.trim();
    const userId = apibUserId.trim();
    if (Boolean(cookie) !== Boolean(userId)) {
      toast.error('apib 登录态需要同时填写 User ID 和 session cookie');
      return;
    }
    const sitesObject = sites as Record<string, unknown>;
    const existingApib = sitesObject['apib.ai'];
    const apibRest: Record<string, unknown> = existingApib
      && typeof existingApib === 'object'
      && !Array.isArray(existingApib)
      ? { ...(existingApib as Record<string, unknown>) }
      : {};
    delete apibRest.sessionCookie;
    delete apibRest.userId;
    if (cookie && userId) {
      sitesObject['apib.ai'] = { ...apibRest, sessionCookie: cookie, userId };
    } else if (Object.keys(apibRest).length > 0) {
      sitesObject['apib.ai'] = apibRest;
    } else {
      delete sitesObject['apib.ai'];
    }
    setSaving(true);
    try {
      const res = await platformFetch('/admin/upstream-balances/config', {
        method: 'PUT',
        body: JSON.stringify({
          username,
          password: password || '******',
          sites,
        }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload?.error || '保存失败');
      }
      toast.success('余额配置已保存');
      setConfigOpen(false);
      await load(true);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const startEditThreshold = (site: UpstreamBalanceSite) => {
    if (site.mode === 'manual') return;
    editDoneRef.current = false;
    setEditingKey(site.key);
    setDraftValue(String(site.thresholdCny));
  };

  const cancelEditThreshold = () => {
    editDoneRef.current = true;
    setEditingKey(null);
    setDraftValue('');
  };

  const commitThreshold = async (site: UpstreamBalanceSite) => {
    if (editDoneRef.current) return;
    editDoneRef.current = true;

    const trimmed = draftValue.trim();
    let nextThreshold: number | null = null;
    if (trimmed !== '') {
      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100000) {
        editDoneRef.current = false;
        toast.error('阈值必须是不小于 0 且不超过 100000 的数字');
        return;
      }
      if (parsed === site.thresholdCny) {
        cancelEditThreshold();
        return;
      }
      nextThreshold = parsed;
    }

    setSavingKey(site.key);
    try {
      const res = await platformFetch('/admin/upstream-balances/threshold', {
        method: 'PUT',
        body: JSON.stringify({ key: site.key, thresholdCny: nextThreshold }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload?.error || '保存阈值失败');
      }
      const effective = payload?.thresholdCny;
      if (typeof effective === 'number' && Number.isFinite(effective)) {
        setData((prev) => (prev ? {
          ...prev,
          sites: prev.sites.map((item) => (item.key === site.key
            ? {
                ...item,
                thresholdCny: effective,
                low: item.balanceCny != null && item.balanceCny < effective,
              }
            : item)),
        } : prev));
      } else {
        await load();
      }
      cancelEditThreshold();
      toast.success('阈值已更新');
    } catch (error) {
      editDoneRef.current = false;
      toast.error(error instanceof Error ? error.message : '保存阈值失败');
    } finally {
      setSavingKey(null);
    }
  };

  const sites = data?.sites || [];
  const lowCount = sites.filter((site) => site.low || site.error).length;
  const newApiCount = sites.filter((site) => site.mode === 'newapi').length;
  const countedSites = sites.filter((site) => site.mode === 'newapi' && site.balanceCny != null);
  const totalBalance = countedSites.reduce((sum, site) => sum + (site.balanceCny ?? 0), 0);
  const excludedCount = sites.length - countedSites.length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-semibold text-light-text">
            <Wallet className="h-5 w-5" />
            上游余额
          </h2>
          <p className="mt-1 text-sm text-light-muted">
            从已启用的图片渠道自动发现站点；new-api 复用登录会话查余额，自建后台标记为手动查看。
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => load(true)}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-lg border border-light-border px-3 py-2 text-sm text-light-text hover:bg-light-hover disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            刷新
          </button>
          <button
            type="button"
            onClick={openConfig}
            className="inline-flex items-center gap-1.5 rounded-lg bg-light-text px-3 py-2 text-sm text-white hover:opacity-90"
          >
            <Settings className="h-4 w-4" />
            配置
          </button>
        </div>
      </div>

      {loadError && (
        <div className="flex items-center justify-between rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>{loadError}</span>
          <button type="button" onClick={() => load()} className="text-red-700 underline">
            重试
          </button>
        </div>
      )}

      {data?.configError && (
        <div className="flex flex-col gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 sm:flex-row sm:items-center sm:justify-between">
          <span>{data.configError}</span>
          <button type="button" onClick={openConfig} className="self-start font-medium underline sm:self-auto">
            去配置
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <div className="col-span-2 rounded-xl border border-light-border bg-white p-4">
          <div className="text-xs text-light-muted">余额合计</div>
          <div className="mt-1 text-3xl font-semibold text-light-text">
            {data && countedSites.length > 0 ? formatCny(totalBalance) : '-'}
          </div>
          <div className="mt-1 text-xs text-light-muted">
            {!data
              ? '等待查询'
              : countedSites.length === 0
                ? '暂无可计入的站点余额'
                : excludedCount > 0
                  ? `已计入 ${countedSites.length} 个站点，${excludedCount} 个未计入`
                  : `已计入 ${countedSites.length} 个站点`}
          </div>
        </div>
        <div className="rounded-xl border border-light-border bg-white p-4">
          <div className="text-xs text-light-muted">发现站点</div>
          <div className="mt-1 text-2xl font-semibold text-light-text">{sites.length}</div>
        </div>
        <div className="rounded-xl border border-light-border bg-white p-4">
          <div className="text-xs text-light-muted">new-api</div>
          <div className="mt-1 text-2xl font-semibold text-light-text">{newApiCount}</div>
        </div>
        <div className="rounded-xl border border-light-border bg-white p-4">
          <div className="text-xs text-light-muted">需处理</div>
          <div className={`mt-1 text-2xl font-semibold ${lowCount > 0 ? 'text-red-600' : 'text-light-text'}`}>{lowCount}</div>
        </div>
        <div className="rounded-xl border border-light-border bg-white p-4">
          <div className="text-xs text-light-muted">最后检查</div>
          <div className="mt-1 text-sm font-medium text-light-text">
            {data?.checkedAt ? formatTime(data.checkedAt) : '-'}
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-light-border bg-white shadow-lg">
        {loading && !data ? (
          <div className="py-12 text-center text-light-muted">
            <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin" />
            正在查询余额...
          </div>
        ) : sites.length === 0 ? (
          <div className="py-12 text-center text-light-muted">
            没有从已启用的图片渠道发现站点。
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[880px]">
              <thead className="bg-light-hover">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-light-text">站点</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-light-text">模式</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-light-text">余额</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-light-text">阈值</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-light-text">状态</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-light-text">渠道</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-light-text">操作</th>
                </tr>
              </thead>
              <tbody>
                {sites.map((site) => (
                  <tr
                    key={site.key}
                    className={`border-t border-light-border ${site.low ? 'bg-red-50' : site.error ? 'bg-amber-50' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-light-text">{site.name}</div>
                      <div className="text-xs text-light-muted">{site.host}</div>
                    </td>
                    <td className="px-4 py-3">{modeBadge(site.mode)}</td>
                    <td className={`px-4 py-3 text-sm font-semibold ${site.low ? 'text-red-700' : 'text-light-text'}`}>
                      {site.mode === 'manual' ? '-' : formatCny(site.balanceCny)}
                    </td>
                    <td className="px-4 py-3 text-sm text-light-muted">
                      {site.mode === 'manual' ? (
                        '-'
                      ) : editingKey === site.key ? (
                        <input
                          type="number"
                          min={0}
                          step={1}
                          autoFocus
                          value={draftValue}
                          disabled={savingKey === site.key}
                          onChange={(event) => setDraftValue(event.target.value)}
                          onBlur={() => {
                            void commitThreshold(site);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              void commitThreshold(site);
                            } else if (event.key === 'Escape') {
                              event.preventDefault();
                              cancelEditThreshold();
                            }
                          }}
                          aria-label={`${site.name} 余额阈值`}
                          className="w-24 rounded-md border border-light-border px-2 py-1 text-sm text-light-text focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:opacity-60"
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => startEditThreshold(site)}
                          title="点击修改阈值，留空保存则恢复默认值"
                          className="group inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-sm text-light-muted hover:bg-light-hover hover:text-light-text"
                        >
                          {formatCny(site.thresholdCny)}
                          {savingKey === site.key ? (
                            <Loader2 className="h-3 w-3 animate-spin text-light-muted" />
                          ) : (
                            <Pencil className="h-3 w-3 text-light-muted opacity-40 transition-opacity group-hover:opacity-100" />
                          )}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3">{statusBadge(site)}</td>
                    <td className="px-4 py-3 text-xs text-light-muted" title={site.channels.join(', ')}>
                      {site.channels.length} 个
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {site.mode === 'manual' ? (
                        <a
                          href={site.manualUrl || site.origin}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                        >
                          后台 <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      ) : (
                        <a
                          href={site.topUpUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                        >
                          充值 <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <BalanceTrendPanel sites={sites.map((site) => ({ key: site.key, name: site.name }))} />

      {configOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-light-border px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-light-text">余额配置</h3>
                <p className="mt-0.5 text-xs text-light-muted">
                  账号密码存数据库；站点从渠道自动发现，覆盖 JSON 一般留空即可。
                </p>
              </div>
              <button type="button" onClick={() => setConfigOpen(false)} className="rounded-lg p-1.5 hover:bg-light-hover">
                <X className="h-5 w-5 text-light-muted" />
              </button>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
              {configLoading ? (
                <div className="py-8 text-center text-light-muted">
                  <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
                  读取配置...
                </div>
              ) : (
                <>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-light-text">通用账号用户名</label>
                    <input
                      value={username}
                      onChange={(event) => setUsername(event.target.value)}
                      placeholder="weiki1998"
                      className="w-full rounded-lg border border-light-border px-3 py-2 text-sm text-light-text focus:outline-none focus:ring-2 focus:ring-blue-200"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-light-text">通用账号密码</label>
                    <input
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder={passwordSet ? '已配置，留空不修改' : '请输入通用账号密码'}
                      className="w-full rounded-lg border border-light-border px-3 py-2 text-sm text-light-text focus:outline-none focus:ring-2 focus:ring-blue-200"
                    />
                    <p className="mt-1 text-xs text-light-muted">
                      只用于 new-api 站点登录查余额，不会返回给前端。上游重置密码后需在这里更新，保存会自动清空旧登录会话。
                    </p>
                  </div>
                  <div className="rounded-lg border border-light-border bg-light-hover/40 p-3">
                    <div className="mb-1 text-sm font-medium text-light-text">apib / APIMart 登录态</div>
                    <p className="mb-2 text-xs text-light-muted">
                      APIMart 开了 Cloudflare 人机验证，服务端无法自动登录，只能复用浏览器里已有的登录态。
                      在 apimart 后台登录后，打开开发者工具，从任意一个 <code>/api/user/...</code> 请求里复制
                      <code className="mx-1">session</code> 的 Cookie 值和 <code>new-api-user</code> 请求头填到这里。
                      两者必须同时填；都留空表示不启用，该站点显示为手动查看。
                    </p>
                    <div className="space-y-2">
                      <input
                        value={apibUserId}
                        onChange={(event) => setApibUserId(event.target.value)}
                        placeholder="new-api-user，如 45523"
                        className="w-full rounded-lg border border-light-border px-3 py-2 text-sm text-light-text focus:outline-none focus:ring-2 focus:ring-blue-200"
                      />
                      <textarea
                        value={apibCookie}
                        onChange={(event) => setApibCookie(event.target.value)}
                        rows={3}
                        spellCheck={false}
                        placeholder="session cookie 的值（带不带 session= 前缀都可以）"
                        className="w-full rounded-lg border border-light-border px-3 py-2 font-mono text-xs text-light-text focus:outline-none focus:ring-2 focus:ring-blue-200"
                      />
                    </div>
                    <p className="mt-1 text-xs text-light-muted">
                      这个会话会过期，页面提示「登录态已过期」时重新登录一次再填一遍即可。
                    </p>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-light-text">站点覆盖（JSON，可选）</label>
                    <textarea
                      value={sitesJson}
                      onChange={(event) => setSitesJson(event.target.value)}
                      rows={10}
                      spellCheck={false}
                      className="w-full rounded-lg border border-light-border px-3 py-2 font-mono text-xs text-light-text focus:outline-none focus:ring-2 focus:ring-blue-200"
                    />
                    <p className="mt-1 text-xs text-light-muted">
                      一般留 <code>{'{}'}</code> 即可。只有余额域名和 API 域名不同（如 frimodel）
                      或需要合并站点时才需要写覆盖；apib 的登录态请用上面的输入框，不要写进这里。
                      阈值也不用写在这里，直接在表格里点「阈值」那一列就能改：
                      <code className="ml-1">{'{"api.frimodel.com":{"origin":"https://platform.frimodel.com"},"api.apimart.ai":{"mergeInto":"apib.ai"}}'}</code>
                    </p>
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-light-border px-5 py-3">
              <button
                type="button"
                onClick={() => setConfigOpen(false)}
                className="rounded-lg border border-light-border px-3 py-2 text-sm text-light-text hover:bg-light-hover"
              >
                取消
              </button>
              <button
                type="button"
                onClick={saveConfig}
                disabled={saving || configLoading}
                className="inline-flex items-center gap-1.5 rounded-lg bg-light-text px-3 py-2 text-sm text-white hover:opacity-90 disabled:opacity-50"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
